import { Illus, LinkAnchor, xlBumps } from '@wrisk/ui-components'
import { DateTime } from 'luxon'
import React, { FunctionComponent, useCallback } from 'react'
import { useNavigate } from 'react-router'

import { useAuth } from '../../../../hooks/auth'
import { useErrorHandlingAsyncCallback } from '../../../../hooks/errors'
import {
  tFormats,
  TKey,
  useWriskTranslation,
} from '../../../../infrastructure/internationalisation'
import { toDateTime } from '../../../../util/date'
import { AppPath } from '../../../AppPath'
import { usePrincipal } from '../../../authentication'
import { Feedback } from '../../../organisms/feedback/Feedback'
import { WriskTrans } from '../../../organisms/trans'
import { FullPage } from '../../../templates'
import { PolicyActivation } from '../activation/PolicyActivation'
import { useProposal } from '../ProposalProvider'
import { PaymentUpsellSection } from '../upsell/PaymentUpsellSection'

const tKey = TKey('pages.payment-success')

export const PolicyActivationCheck: FunctionComponent = () => {
  const { proposal } = useProposal()
  return proposal.policyActivationRequired ? <PolicyActivation /> : <PaymentSuccess />
}

export const PaymentSuccess: FunctionComponent = () => {
  const navigate = useNavigate()

  const { t } = useWriskTranslation()

  const auth = useAuth()

  const { proposal } = useProposal()
  const { isAuthenticated } = usePrincipal()

  const onContinue = useCallback(() => navigate(AppPath.HOME), [navigate])
  const onSignInCallback = useErrorHandlingAsyncCallback(() => auth.signIn(AppPath.HOME))

  const startAt = proposal.startAt

  const subheader =
    startAt && DateTime.fromISO(startAt) > DateTime.local() ? (
      <WriskTrans
        t={t}
        i18nKey={tKey('deferred')}
        values={{
          startAt: t(tFormats('datetime.medium'), { value: toDateTime(startAt) }),
        }}
      />
    ) : (
      t(tKey('subheader'))
    )

  const actionToRender = isAuthenticated ? (
    <LinkAnchor onClick={onContinue} variant='standalone'>
      {t(tKey('actions.continue'))}
    </LinkAnchor>
  ) : (
    <LinkAnchor onClick={onSignInCallback.execute} variant='standalone'>
      {t(tKey('actions.sign-in'))}
    </LinkAnchor>
  )

  return (
    <FullPage
      pageId='payment-page'
      header={t(tKey('header'))}
      subheader={subheader}
      upperHeader={<Illus alt='Success' data-testid='bind-success' illus='bindSuccess' />}
    >
      {actionToRender}

      <PaymentUpsellSection mt={xlBumps} proposal={proposal} />

      <Feedback type='bind' tags={{ policyId: proposal.policyId ?? '' }} />
    </FullPage>
  )
}
